import { computed, toValue, type ComputedRef, type MaybeRefOrGetter } from 'vue'
import { useAppQuery } from '@/composables/useAppQuery'
import {
  getLedgerAbnormalQueryOptions,
  getLedgerCorrectionQueryOptions
} from '@/queries/ledger-anomaly/queries'
import type {
  LedgerAbnormalResType,
  LedgerCorrectionResType
} from '@/models/types/ledgerAnomaly.types'

/**
 * 帳務異常判定與修正紀錄的來源（OP#1855）
 *
 * 只負責取資料，不做歸戶：事件要落在哪一個日列，取決於該列的結算區間，
 * 由呼叫端用 `utils/ledger/ledgerExclusion` 的純函式計算。
 *
 * 獨立成一個 composable 是因為帳務查詢的選物販賣機與兌幣機都要用，
 * 而且這兩支查詢失敗時不該影響帳務數字本身的呈現。
 */
export type LedgerExclusionSource = {
  abnormals: ComputedRef<LedgerAbnormalResType[]>
  corrections: ComputedRef<LedgerCorrectionResType[]>
}

export const useLedgerExclusion = (params: {
  pcbId: string
  startAt: MaybeRefOrGetter<string>
  endAt: MaybeRefOrGetter<string>
  enabled: MaybeRefOrGetter<boolean>
}): LedgerExclusionSource => {
  // 後端以 egmId 稱呼機台，與前端的 pcbId 是同一個識別碼
  const queryParams = computed(() => ({
    egmId: params.pcbId,
    startAt: toValue(params.startAt),
    endAt: toValue(params.endAt)
  }))

  // 輔助性查詢：失敗時帳務數字仍要正常顯示，不跳錯誤訊息打擾使用者
  const abnormalQuery = useAppQuery(
    () => ({
      ...getLedgerAbnormalQueryOptions(queryParams.value),
      enabled: toValue(params.enabled)
    }),
    { errorMessage: false }
  )

  const correctionQuery = useAppQuery(
    () => ({
      ...getLedgerCorrectionQueryOptions(queryParams.value),
      enabled: toValue(params.enabled)
    }),
    { errorMessage: false }
  )

  return {
    abnormals: computed(() => toValue(abnormalQuery.data) ?? []),
    corrections: computed(() => toValue(correctionQuery.data) ?? [])
  }
}
